const AUTH_MODE = (process.env.REACT_APP_AUTH_MODE || 'keycloak').toLowerCase();

let keycloak = null;
let KeycloakConstructor = null;
let initPromise = null;
let didInit = false;

export function isKeycloakMode() {
  return AUTH_MODE === 'keycloak';
}

function getKeycloakConstructor() {
  if (KeycloakConstructor) return KeycloakConstructor;

  // Lazy-load to avoid Jest choking on keycloak-js ESM when running in legacy mode.
  // eslint-disable-next-line global-require
  const mod = require('keycloak-js');
  KeycloakConstructor = mod && mod.default ? mod.default : mod;
  return KeycloakConstructor;
}

export function getKeycloak() {
  if (!isKeycloakMode()) return null;
  if (keycloak) return keycloak;

  // Default assumes local reverse proxy / hosts file setup.
  // If Keycloak is exposed elsewhere (e.g. behind a reverse proxy under /login),
  // configure `REACT_APP_KEYCLOAK_URL` explicitly.
  const url = process.env.REACT_APP_KEYCLOAK_URL || 'http://keycloak.localhost';
  const realm = process.env.REACT_APP_KEYCLOAK_REALM || 'albert';
  const clientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'schreiberlein-frontend';

  const Keycloak = getKeycloakConstructor();
  keycloak = new Keycloak({ url, realm, clientId });
  return keycloak;
}

export async function initKeycloak() {
  const kc = getKeycloak();
  if (!kc) return { authenticated: false };

  if (initPromise) return initPromise;
  if (didInit) return { authenticated: Boolean(kc.authenticated) };

  // PKCE S256 needs WebCrypto SubtleCrypto (SHA-256), which is usually only available
  // in secure contexts (HTTPS or localhost). On plain HTTP over LAN IP, disable PKCE.
  // keycloak-js v26 accepts only 'S256' or false.
  const resolvePkceMethod = () => {
    if (typeof window === 'undefined') return undefined;
    const c = window.crypto;
    if (c && c.subtle && typeof c.subtle.digest === 'function') return 'S256';
    return false;
  };

  const pkceMethod = resolvePkceMethod();
  const options = {
    onLoad: 'check-sso',
    checkLoginIframe: false,
    silentCheckSsoRedirectUri: typeof window !== 'undefined'
      ? `${window.location.origin}/silent-check-sso.html`
      : undefined,
    // Default keycloak-js response mode is 'fragment'. For authorization code flow,
    // 'query' is the more common and less error-prone mode with proxies.
    responseMode: 'query',
    pkceMethod,
  };

  initPromise = kc
    .init(options)
    .then((authenticated) => {
      didInit = true;
      return { authenticated };
    })
    .catch((err) => {
      // keycloak-js throws if init() is called more than once on the same instance.
      // In dev with React refresh / double effects, this can happen. Treat it as
      // "already initialized" and continue with best-effort auth state.
      const msg = String(err?.message || err || '');
      if (msg.includes('only be initialized once')) {
        didInit = true;
        return { authenticated: Boolean(kc.authenticated) };
      }

      initPromise = null;
      didInit = false;
      throw err;
    });

  return initPromise;
}

export async function ensureFreshToken(minValiditySeconds = 30) {
  const kc = getKeycloak();
  if (!kc) return null;
  if (!kc.authenticated) {
    // On callback routes (/loginSuccess with ?code=...), the token may not be
    // processed yet. initKeycloak() does not auto-redirect anymore, so it's safe
    // to call here to complete the code->token exchange.
    try {
      await initKeycloak();
    } catch {
      // ignore; caller will treat as no token available
    }
  }
  if (!kc.authenticated) return null;
  await kc.updateToken(minValiditySeconds);
  return kc.token;
}

export async function startKeycloakPasswordChange(redirectUri) {
  const kc = getKeycloak();
  if (!kc) return false;
  await initKeycloak();
  await kc.login({
    action: 'UPDATE_PASSWORD',
    redirectUri: redirectUri || (typeof window !== 'undefined' ? `${window.location.origin}/account` : undefined),
  });
  return true;
}
